import React, { useContext, useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import { ReportingQuery } from '~/QueryHooks/ReportingQuery';
import i18n from '~/components/i18n';
import { useAuth } from '~/routes/components/AuthProvider';
import TableComponent from '~/components/TableComponent';
import dayjs from 'dayjs';
import InfoPopup from '~/components/InfoPopup';
import { StyledFiltersOptions, StyledFilterWrapper, StyledHeader } from './style'
import { El } from '~/components/Layout/style';
import { DownloadService } from '~/api/services/downloadFiles';
import { ReportingService, TradActOfferType, TradActiFilterType } from '~/api/services/reporting';
import Dialog from '~/components/Dialog';
import TradingChart from './TradingChart';
import { LIGHT_USER_ROLE_SLUG, OPERATIVE_ROLE_SLUG, getReportingFilters } from '~/Constants';
import { Checkbox, Dropdown, Icon, Label } from 'semantic-ui-react';
import { ThemeContext } from 'styled-components';
import Filters from './Filters';
import { capitalizeFirstLetter, getPrefixData, getTradingColumnName, handleErrors } from '~/utils/helpers';
import { useParams } from 'react-router-dom';
import ExcelExport from '~/components/ExcelExport';
import { FixedPricePrefixType } from '~/api/services/dashboard';

const defaultFilterValues = {
  yearFrom: '',
  yearTo: '',
  monthFrom: '',
  monthTo: '',
  farmerGroupId: '',
  farmerId: '',
  tradingVolumeTypeId: '',
  milkTypeId: ''
}

const farmerFilters = [
  {
    key: 'farmerGroupId',
    placeholder: 'Search farmer group',
    type: 'select',
    filterId: 'search-farmer-groups-trading-activity'
  },
  {
    key: 'farmerId',
    placeholder: 'Search farmers',
    type: 'select',
    filterId: 'search-farmers-trading-activity'
  },
  {
    key: 'milkTypeId',
    id: 'milkTypeId',
    placeholder: 'Milk types'
  }
]

const TradingActivities: React.FunctionComponent = () => {
  const actions = [
    {
      popupMessage: i18n.t('Show more'),
      type: 'icon',
      name: 'eye',
      size: 'large',
      actionType: 'show'
    },
    {
      popupMessage: i18n.t('Results'),
      type: 'icon',
      name: 'clipboard outline',
      size: 'large',
      actionType: 'results'
    }
  ]
  const {
    systemData: {
      operativeUsersCanSeeFarmersInfo,
      logged: { roleSlug },
      amountPrice,
      amountVolume
    }
  } = useAuth()
  const { systemData: { amountUnit, languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const { firstColor } = useContext(ThemeContext)
  const { t } = useTranslation();
  const priceLabel = `${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit}`
  const { prefix } = useParams()
  const tradingPriceColumns = getTradingColumnName(prefix, priceLabel, amountUnit)
  const { volumePriceLabel, priceTitle, slug } = getPrefixData(prefix)
  const reportingFilters = getReportingFilters()

  const [dataFiltered, setDataFiltered] = useState<TradActiFilterType>(defaultFilterValues)
  const [tradingActivities, setTradingActivities] = useState<TradActOfferType[]>([])
  const [month, setMonth] = useState(null)
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [isBusy, setIsBusy] = useState(false)
  const farmerFiltersDisabled = roleSlug === OPERATIVE_ROLE_SLUG && !operativeUsersCanSeeFarmersInfo
  const [checkedFilters, setCheckedFilters] = useState([])

  const fatProteinColumns = prefix === FixedPricePrefixType.FatProtein ? getFatProteinColumns() : []
  const fatProteinTradColumns = prefix === FixedPricePrefixType.FatProtein ? getFatProteinTradColumns() : []

  const tableColumnHeaders = [
    {
      name: t('Delivery month'),
      key: 'month',
      type: 'dateRibbon',
      format: 'MMM YYYY'
    },
    {
      name: tradingPriceColumns.average.name,
      key: 'avgPrice',
      type: 'no-unit-number'
    },
    ...fatProteinColumns,
    {
      name: t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'offeredAmount',
      type: 'number-whole'
    },
    {
      name: t('Max trading amount (in {{amountUnit}})', { amountUnit }),
      key: 'maxAmount',
      type: 'number-whole'
    },
    {
      name: tradingPriceColumns.priceBrackets.name,
      key: 'confirmedAmount',
      type: 'offered-amount'
    },
    {
      name: t('Remaining amount [in {{amountUnit}} / in % ]', { amountUnit }),
      key: 'remainingAmount',
      type: 'remaining-amount'
    },
    {
      name: t('Actions')
    }
  ]

  const tableColumnHeadersActivitie = [
    {
      name: t('Delivery date'),
      key: 'confirmationDate',
      type: 'dateRibbon',
      format: 'DD.MM.YYYY HH:mm',
      info: t('Date and time when your dairy has accepted your offered amount totally, partially or not at all within the trading slot ({{volumePriceLabel}}).', { volumePriceLabel })
    },
    {
      name: tradingPriceColumns.trading.name,
      key: 'price',
      info: tradingPriceColumns.trading.info,
      type: 'no-unit-number'
    },
    ...fatProteinTradColumns,
    {
      name: t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'number-whole',
      info: t('Offered amount which you have submitted within the trading slot.')
    },
    {
      name: tradingPriceColumns.price.name,
      key: 'confirmedAmount',
      type: 'number-whole',
      info: t('The amount your dairy has actually confirmed (at most equivalent to offered amount).')
    },
    {
      name: t('Offer ID'),
      key: 'uniqueId'
    },
    {
      name: t('Offer date', { amountUnit }),
      key: 'createdAt',
      type: 'date',
      format: 'DD.MM.YYYY HH:mm',
      info: t('Date and time when you have submitted your offered amount within the trading slot.')
    },
    {
      name: t('Supplier No.'),
      key: 'supplierNo'
    },
    {
      name: t('Trader'),
      key: 'name'
    }
  ]

  const {
    data: tradingActivityData,
    isLoading: isBusyActivityData,
    isFetching: isFetching,
    refetch: fetchFilteredData
  } = ReportingQuery.getTradingData(
    prefix,
    {
      ...dataFiltered,
      monthFrom: dataFiltered.monthFrom ? dataFiltered.monthFrom : null,
      monthTo: dataFiltered.monthTo ? dataFiltered.monthTo : null
    })

  const {
    data: tradingSettings,
    isLoading: isBusyActivitySettings,
    refetch: refetchSettings
  } = ReportingQuery.getTradingSettings(prefix)

  useEffect(() => {
    const filters = localStorage.getItem('reportingFilters')
    if (filters) {
      const filtersParsed = JSON.parse(filters)
      filtersParsed.forEach(filter => {
        const indexFilter = reportingFilters.findIndex(farmerFilter => farmerFilter.key === filter.key)
        if (indexFilter >= 0) {
          filter.placeholder = reportingFilters[indexFilter].placeholder
        }
      })
      setCheckedFilters(filtersParsed)
    }
  }, [])

  useEffect(() => {
    refetchSettings()
  }, [t])

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered, prefix])

  function getFatProteinColumns() {
    return [
      {
        name: t('Average fat price (€/100kg)'),
        key: 'avgFatPrice',
        type: 'no-unit-number'
      },
      {
        name: t('Average protein price (€/100kg)'),
        key: 'avgProteinPrice',
        type: 'no-unit-number'
      }
    ]
  }

  function getFatProteinTradColumns() {
    return [
      {
        name: t('Fat price (€/100kg)'),
        key: 'fatPrice',
        type: 'no-unit-number'
      },
      {
        name: t('Protein price (€/100kg)'),
        key: 'proteinPrice',
        type: 'no-unit-number'
      }
    ]
  }

  async function handleDownloadTradingActivities(month) {
    setIsBusy(true)
    try {
      const monthFormated = dayjs(month).format('YYYY-MM-DD')
      await DownloadService.downloadFile(`reporting/${prefix}/trading-activities/offers/export/excel`, 'trading_activitie', { ...dataFiltered, month: monthFormated })
    }
    catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function handleShowTradingActivitie(row, type) {
    const monthFormated = dayjs(row?.month).format('YYYY-MM-DD')
    const { month } = row
    setLoadingAction({
      value: true,
      type,
      id: row.month,
      idKey: 'month'
    })
    try {
      const response = await ReportingService(prefix).getTradingOffers({ ...dataFiltered, month: monthFormated })
      setTradingActivities(response.data)
      setMonth(month)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      value: false,
      type: '',
      id: -1,
      idKey: ''
    })
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'show':
        return handleShowTradingActivitie(row, type)
      case 'results':
        return handleDownloadTradingActivities(row.month)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      rows={tradingActivityData}
      handleClickAction={(row, type) => handleClickAction(row, type)}
      removePagination
      animated
      loading={isBusy}
      idKey={'month'}
      isFetchingData={isFetching}
      loadingAction={loadingAction}
      actions={actions}
      total={tradingActivityData.length}
    />
  }

  function renderTitle() {
    return <StyledHeader>
      <div className='title'>{t('Reporting - Trading activities')}</div>
      <InfoPopup
        content={t('Detailed overview over the {{volumePriceLabel}} confirmed by the dairy \n\
          (including average {{priceTitle}}) for the according trading months including all trading activities which were carried out.', { volumePriceLabel, priceTitle })}
        styleIcon={{ cursor: 'pointer', fontSize: '1.4em', marginLeft: 5 }}
      />
    </StyledHeader>
  }

  function renderActions() {
    return <>
      {!farmerFiltersDisabled && <>
        <El.PageHeaderActionButton
          size='large'
          disabled={checkedFilters.length === 0}
          onClick={() => setDataFiltered(defaultFilterValues)}
        >
          {t('Reset')}
        </El.PageHeaderActionButton>
        <StyledFiltersOptions
          text={t('Filters')}
          icon='filter'
          floating
          labeled
          button
          closeOnChange={false}
          multiple
          className='icon'
          style={{ background: firstColor, color: 'white' }}
        >
          <Dropdown.Menu>
            <Dropdown.Menu scrolling>
              {reportingFilters.map((filter, indexFilter) => {
                return <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                  }}
                  key={filter.key}
                >
                  <Checkbox
                    onClick={(e, data) => {
                      e.stopPropagation()
                      e.preventDefault()
                      if (data.checked) {
                        const newArray = [...checkedFilters];
                        newArray.splice(indexFilter, 0, filter);
                        setCheckedFilters(newArray)
                        localStorage.setItem('reportingFilters', JSON.stringify([...checkedFilters, filter]))
                      } else {
                        setCheckedFilters(checkedFilters.filter(filterChecked => filterChecked.key !== filter.key))
                        localStorage.setItem('reportingFilters', JSON.stringify(checkedFilters.filter(filterChecked => filterChecked.key !== filter.key)))
                      }
                    }}
                    checked={Boolean(checkedFilters.find(filterChecked => filterChecked.key === filter.key))}
                    label={t(filter.placeholder)}
                  />
                </Dropdown.Item>
              })}
            </Dropdown.Menu>
          </Dropdown.Menu>
        </StyledFiltersOptions>
      </>}
      {roleSlug !== LIGHT_USER_ROLE_SLUG && <ExcelExport
        data={dataFiltered}
        fileName={'trading_activities'}
        buttonType={'icon'}
        icon={'file excel outline'}
        url={`reporting/${prefix}/trading-activities/export/excel`}
      />
      }
    </>
  }

  function renderShowModal() {
    return <Dialog
      open={Boolean(month)}
      textClose={t('Close')}
      htmlContent
      handleClose={() => setTradingActivities(null)}
      textHeader={capitalizeFirstLetter(dayjs(month).format('MMM YYYY'))}
      textContent={<TableComponent
        tableColumnHeaders={tableColumnHeadersActivitie}
        rows={tradingActivities}
        removePagination
        total={tradingActivities.length}
      />}
      type={'dialog'}
      style={{ width: '90%' }}
    />
  }

  function renderFilters() {
    return <Filters
      checkedFilters={checkedFilters}
      tradingSettings={tradingSettings}
      fetchFilteredData={(data) => {
        setDataFiltered(data)
      }}
      dataFiltered={dataFiltered}
      slug={slug}
    />
  }

  function getSelectValue(filter) {
    const element = document.getElementById(filter.filterId)
    const childElement = element?.querySelector('.divider.text')
    return childElement?.innerHTML
  }

  function getFilterValue(filter) {
    if (filter.type === 'date') {
      const monthNumber = dataFiltered[filter.monthKey]
      const monthName = monthNumber ? dayjs().month(monthNumber - 1).locale(languageLocal.code).format('MMMM') : ''
      return `${dataFiltered[filter.key]}${monthName ? ` [${monthName}]` : ''}`
    }
    return dataFiltered[filter.key]
  }

  function renderFilterValues() {
    return [...checkedFilters, ...farmerFilters].map(filter => {
      const filterInputKey = filter.key
      const value = filter.type === 'select' ? getSelectValue(filter) : getFilterValue(filter)
      return value && value !== '' && value !== t(filter.placeholder) && <Label style={{ background: firstColor, color: 'white' }} as='a'>
        {`${t(filter?.labelValue || filter?.placeholder)} (${value})`}
        <Icon name='delete' onClick={() => {
          setDataFiltered({
            ...dataFiltered,
            [filterInputKey]: ''
          })
        }} />
      </Label>
    })
  }

  return <Layout
    segmented
    isBusy={isBusyActivityData || isBusyActivitySettings}
    actions={renderActions()}
    filters={renderFilters()}
    title={renderTitle()}
    actionAlignToTop
  >
    {tradingActivities && renderShowModal()}
    {tradingActivityData && tradingSettings && <TradingChart
      tradingActivityData={tradingActivityData}
      tradingSettings={tradingSettings}
    />}
    <StyledFilterWrapper>
      {!(isBusyActivityData || isBusyActivitySettings) && renderFilterValues()}
    </StyledFilterWrapper>
    {tradingActivityData && renderTable()}
  </Layout>;
};

export default TradingActivities;